import type {TokenProps} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import type {BetterSystemStyleObject} from '@primer/styled-react'
import {Token} from '@primer/styled-react'
import {clsx} from 'clsx'
import {useEffect, useRef, useState} from 'react'

import styles from './IssueFieldSingleSelectValueToken.module.css'

// TODO issue_fields rename the @github-ui/issue-type-token to @github-ui/issue-tokens and move that to this package
export type IssueFieldSingleSelectValueTokenProps = {
  name: string
  color: string
  getTooltipText: (isTextTruncated: boolean) => string | undefined
  sx?: BetterSystemStyleObject
  containerClassName?: string
} & Pick<TokenProps, 'size'>

export const IssueFieldSingleSelectValueToken = ({
  name,
  color,
  getTooltipText,
  size,
  sx,
  containerClassName,
}: IssueFieldSingleSelectValueTokenProps) => {
  const tokenRef = useRef<HTMLElement>(null)
  const [truncated, setTruncated] = useState(false)

  useEffect(() => setTruncated(isTextTruncated(tokenRef.current)), [setTruncated, tokenRef])

  const tooltipText = getTooltipText(truncated)

  const internal = (
    <Token
      className={styles.token}
      data-color={color}
      ref={tokenRef}
      sx={sx}
      // the span in the text is allowing us to check if the text is truncated
      text={<span className={styles.tokenText}>{name}</span>}
      as={tooltipText ? 'button' : 'span'}
      {...(tooltipText && {
        type: 'button',
        'aria-label': `${name} token`,
      })}
    />
  )

  return (
    <div className={clsx(styles.container, containerClassName)} data-size={size}>
      {tooltipText ? <Tooltip text={tooltipText}>{internal}</Tooltip> : internal}
    </div>
  )
}
// This is not bullet proof, since the true inner width of the element is rounded down.
// I think it's pretty good though. See discussion here https://stackoverflow.com/questions/64689074/how-to-check-if-text-is-truncated-by-css-using-javascript
const isTextTruncated = (e: HTMLElement | null): boolean => {
  if (!e || !e.firstElementChild) return false
  return e.firstElementChild.scrollWidth > e.firstElementChild.clientWidth
}
